import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import * as Sentry from "@sentry/react";

import type { Drawing } from "@/components/records/image-markup-editor/types";
import type { CropRect } from "@/components/records/image-markup-editor/crop-overlay";

const DB_NAME = "otus-offline";
// v3: image bytes are persisted as `ArrayBuffer` + MIME type, never raw `Blob`.
// iOS Safari stores IndexedDB `Blob`s as separate backing files referenced by
// handle; those handles are not reliably isolated across a clear-data +
// DB-recreate cycle, so a record can resolve to another project's photo. An
// `ArrayBuffer` is structured-cloned inline and cannot cross-reference.
// v4: adds the `pendingAppends` store — the durable append outbox, a sibling
// to `pendingRecords` (it queues photos ADDED to an existing, synced record).
// Every pre-existing store survives the upgrade untouched.
// v5: adds the `pendingDeletes` store. The per-photo delete feature that used it
// (the delete-outbox hooks + image-viewer UI) was REVERTED, but the store and
// the version are intentionally RETAINED: IndexedDB versions can only increase,
// so downgrading to v4 would throw `VersionError` on any client that already
// opened v5. Do not remove this store or lower DB_VERSION. The `PendingDelete`
// type and `pendingDeletes` helpers below are kept with it so the schema is
// ready to re-use if the feature is relanded.
const DB_VERSION = 5;
/** MIME type stored when a blob carries none — keeps rehydration lossless. */
const DEFAULT_BLOB_TYPE = "application/octet-stream";

export interface CachedIdentity {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  lastValidatedAt: number;
}

export type PinStatus = "syncing" | "ready" | "error";

/**
 * Why a project is cached offline. `"manual"` = the user tapped Pin (a deliberate
 * "keep this offline"); `"auto"` = cached because the user opened the project (see
 * the auto-cache-on-open spec). Manual wins: an auto trigger never downgrades a
 * manual pin. Recorded now as the seam for a future expiry/LRU policy — today both
 * sources behave identically.
 */
export type PinSource = "manual" | "auto";

export interface PinProgress {
  recordsDownloaded: number;
  recordsTotal: number;
  plansDownloaded: number;
  plansTotal: number;
}

export interface PinnedProject {
  projectId: string;
  pinned: true;
  lastSyncedAt: number | null;
  status: PinStatus;
  progress: PinProgress;
  sizeBytes: number;
  /**
   * Why this project is cached — see {@link PinSource}. Optional: rows written
   * before this field existed lack it and are read as `"manual"`. No `DB_VERSION`
   * bump needed — schema-less value store, like {@link StoredAsset.provisional}.
   */
  source?: PinSource;
}

/**
 * A cached asset as callers see it — `getAsset` returns this shape and
 * `putAsset` accepts it. The `blob` is rehydrated on read / serialized on
 * write; the on-disk row ({@link StoredAssetRow}) holds an `ArrayBuffer`.
 */
export interface StoredAsset {
  key: string;
  blob: Blob;
  contentType: string;
  fetchedAt: number;
  projectId: string;
  /**
   * True for a local-blob seed of a just-synced offline-created record,
   * awaiting its canonical server thumbnail. Absent / false = durable. See
   * [[offline-asset-cache]].
   */
  provisional?: boolean;
}

/**
 * The on-disk `assets` row. `data` is the asset's bytes as a structured-clone
 * inline `ArrayBuffer` — never a `Blob` (see {@link DB_VERSION}). The store is
 * dropped and recreated on the v3 upgrade, so no legacy `Blob` rows survive.
 */
interface StoredAssetRow {
  key: string;
  data: ArrayBuffer;
  contentType: string;
  fetchedAt: number;
  projectId: string;
  provisional?: boolean;
}

/**
 * Annotation payload for a queued record — mirrors the `AnnotationPayload`
 * type in `use-submit-record.ts` and exactly what `appendAnnotations`
 * serializes, so the outbox drain can rebuild the upload's
 * `annotations` field.
 */
export interface PendingRecordAnnotations {
  drawings: Drawing[];
  crop?: CropRect;
  /** Clockwise quarter-turns applied before crop/drawings; omitted when 0. */
  rotation?: number;
  downscale_max_px?: number;
}

export type PendingPhotoStatus = "pending" | "synced" | "failed";

/**
 * Outcome of {@link removePhotoFromPendingRecord}:
 *  - `"removed"`   — the photo was spliced out of the draft.
 *  - `"synced"`    — refused: the photo already uploaded server-side; removing
 *                    it locally would leave a ghost (see the function doc).
 *  - `"last-photo"`— refused: it was the lone photo (a record delete instead).
 *  - `"not-found"` — the entry or photo no longer exists.
 */
export type RemovePendingPhotoResult =
  | "removed"
  | "synced"
  | "last-photo"
  | "not-found";

/**
 * Outcome of {@link updatePendingRecordPhoto}:
 *  - `"updated"`   — the photo's markup fields were written (revision bumped).
 *  - `"synced"`    — refused: the photo already uploaded server-side; the
 *                    drain's mid-upload reconcile replays only the COVER's
 *                    markup bytes, so a local edit of an already-uploaded
 *                    photo would be silently lost when the batch lands.
 *  - `"not-found"` — the entry (or that photo) no longer exists.
 */
export type UpdatePendingPhotoResult = "updated" | "synced" | "not-found";

/** The markup fields a per-photo re-edit writes — see {@link updatePendingRecordPhoto}. */
export interface PendingPhotoMarkupPatch {
  annotatedBlob: Blob;
  originalBlob: Blob;
  originalFileName: string | null;
  annotations: PendingRecordAnnotations;
}

/**
 * One photo of a multi-photo pending record. `photoId` is the
 * `client_photo_uuid` (= `CapturedPhoto.id`) — the per-photo
 * `X-Idempotency-Key` reused across every upload attempt of this photo, so a
 * retry can never append it twice. Each photo uploads independently to the
 * v2.0 record endpoint under the record's shared `client_record_uuid` (= the
 * owning `PendingRecord.clientUuid`); `uploadStatus` persists per-photo
 * progress so a boot or retry re-drains only the photos that have not landed.
 */
export interface PendingPhoto {
  photoId: string;
  /** Client-rendered annotated bytes — the multipart `image` field. */
  annotatedBlob: Blob;
  /** Original raw photo — the multipart `original` field. */
  originalBlob: Blob;
  /** Filename of the original capture (`File.name`); null for a legacy entry. */
  originalFileName: string | null;
  /** Annotations payload — serialized into the `annotations` field. */
  annotations: PendingRecordAnnotations;
  /** Hint for the `taken_at_hint` field; `taken_at_source_hint` is derived. */
  takenAtHint: string | null;
  uploadStatus: PendingPhotoStatus;
  /** Server reason set when `uploadStatus === "failed"` (permanent 4xx). */
  error?: string | null;
}

export type PendingRecordStatus = "pending" | "failed";

/**
 * A locally-queued record awaiting upload. The outbox is durable across
 * reloads and crashes; the drain replays `pending` entries until they land
 * on the server. Carries everything the outbox drain needs to
 * reconstruct the multipart upload built by `useSubmitRecord.submitRecord`.
 */
/**
 * An issue to raise in the same request that creates the record.
 *
 * Queued alongside the record rather than posted separately: creation always
 * goes through this outbox, so a separately-posted issue would have to wait
 * for the record to sync and then chase its server id. The backend takes the
 * whole thing on the create request instead.
 *
 * `clientUuid` is the issue's own replay guard, independent of the record's.
 */
export interface PendingIssue {
  clientUuid: string;
  title: string;
  description: string;
  assignedToId: string | null;
  dueDate: string | null;
}

export interface PendingRecord {
  /** Local id AND the `X-Idempotency-Key` reused for every upload attempt. */
  clientUuid: string;
  /**
   * Set when the capture also raised an issue. Absent on every record created
   * before this shipped, and on every record where the toggle stayed off —
   * so it is optional rather than nullable-required, and no IDB version bump
   * is needed (no index changes, and older builds simply ignore the field).
   */
  issue?: PendingIssue | null;
  projectId: string;
  /** Client-rendered annotated bytes — the multipart `image` field. */
  annotatedBlob: Blob;
  /** Original raw photo — the multipart `original` field. */
  originalBlob: Blob;
  /**
   * Filename of the original capture (`File.name`); null for a legacy entry.
   * Carried so the multipart upload sends the real filename — the server
   * validates uploads by extension, so this value is never sent raw: the
   * outbox builders normalise it through `uploadImageFilename`, which
   * supplies an extension (derived from the blob's MIME type) whenever this
   * name lacks an acceptable one.
   */
  originalFileName: string | null;
  planId: string | null;
  pinX: number | null;
  pinY: number | null;
  /**
   * Plan-relative camera bearing in degrees `[0, 360)` (see
   * [[directional-pins]]). Optional/nullable — a legacy entry written before
   * this field existed reads as `undefined` and is treated as no direction.
   * Threads through the outbox alongside the pin coordinates so an offline-set
   * direction renders before sync.
   */
  pinBearing?: number | null;
  tags: string[];
  /** Annotations payload — serialized into the `annotations` field. */
  annotations: PendingRecordAnnotations;
  /** Hint for the `taken_at_hint` field; `taken_at_source_hint` is derived. */
  takenAtHint: string | null;
  /** Built dynamic-metadata object — what `buildMetadata` produces. */
  metadata: Record<string, string | number | boolean | string[]>;
  status: PendingRecordStatus;
  /** Starts at 0; bumped on each local edit before the record syncs. */
  revision: number;
  /** Set true when a pending record is deleted (in-flight-upload reconcile). */
  deleted: boolean;
  createdAt: number;
  /**
   * Present iff this is a MULTI-photo record. Each photo uploads independently
   * to the v2.0 endpoint under the shared `clientUuid` (= `client_record_uuid`).
   * When absent or empty the entry is a legacy row — `fromStoredPendingRecord`
   * normalizes it on read into a 1-element `photos[]` (using `clientUuid` as
   * the sole `photoId`) and drains it via v2.0 like any other entry. Invariant
   * for a multi entry: the top-level
   * `annotatedBlob` / `originalBlob` / `annotations` / `takenAtHint` mirror the
   * COVER (`photos[0]`) so the existing single-photo tile and modal-open code
   * render the cover with no special-casing.
   */
  photos?: PendingPhoto[];
  /**
   * Server record id of a MULTI entry, recorded the first time any of its
   * photos syncs (the backend's get-or-create returns it). Lets a later delete
   * issue the compensating server DELETE BY ID — no recovery re-POST, which
   * could re-create a ghost if a prior delete already landed. Absent for a
   * single-photo entry, and until the first photo of a multi entry lands.
   */
  serverRecordId?: string | null;
}

/** The subset of {@link PendingRecord} fields editable on a local draft. */
export type PendingRecordEditableFields = Pick<
  PendingRecord,
  | "annotatedBlob"
  | "originalBlob"
  | "originalFileName"
  | "planId"
  | "pinX"
  | "pinY"
  | "pinBearing"
  | "tags"
  | "annotations"
  | "takenAtHint"
  | "metadata"
>;

/**
 * The on-disk `pendingRecords` row. Image bytes are stored as inline
 * `ArrayBuffer`s with their MIME type as a sibling string (see
 * {@link DB_VERSION}). Unlike `assets`, this store is NOT recreated on the v3
 * upgrade — it holds un-uploaded captures, and dropping it would be data loss.
 * So the blob fields are typed `ArrayBuffer | Blob`: a legacy v2 row still
 * holds raw `Blob`s, and the read path ({@link fromStoredBytes}) tolerates
 * both. New writes always store `ArrayBuffer` + type.
 */
/**
 * The on-disk shape of a {@link PendingPhoto}: image bytes as inline
 * `ArrayBuffer`s with their MIME type as a sibling string, exactly like
 * {@link StoredPendingRecord}.
 */
interface StoredPendingPhoto
  extends Omit<PendingPhoto, "annotatedBlob" | "originalBlob"> {
  annotatedBlob: ArrayBuffer | Blob;
  annotatedBlobType?: string;
  originalBlob: ArrayBuffer | Blob;
  originalBlobType?: string;
}

interface StoredPendingRecord
  extends Omit<PendingRecord, "annotatedBlob" | "originalBlob" | "photos"> {
  annotatedBlob: ArrayBuffer | Blob;
  annotatedBlobType?: string;
  originalBlob: ArrayBuffer | Blob;
  originalBlobType?: string;
  /** Per-photo rows for a multi-photo record; absent for single-photo. */
  photos?: StoredPendingPhoto[];
}

/**
 * Annotation payload for a queued APPEND — the exact shape the v2.0 append
 * request serializes into its `annotations` field (`drawings` + optional
 * `crop` + optional `downscaleMaxPx`). Mirrors {@link PendingRecordAnnotations}
 * so `buildAppendFormData` can rebuild the upload byte-for-byte against the
 * online append in `use-append-photo.ts`.
 */
export interface PendingAppendAnnotations {
  drawings: Drawing[];
  crop?: CropRect;
  /** Clockwise quarter-turns applied before crop/drawings; omitted when 0. */
  rotation?: number;
  downscaleMaxPx?: number;
}

export type PendingAppendStatus = "pending" | "failed";

/**
 * A locally-queued photo awaiting append to an EXISTING, synced record. The
 * append outbox is a durable sibling of `pendingRecords` (see
 * [[append-photo-outbox]]): same retry-forever / idempotency discipline, but
 * a different optimistic model — it augments a real record rather than
 * synthesizing a new tile. The drain replays `pending` entries until each
 * photo lands on the server's v2.0 record endpoint.
 */
export interface PendingAppend {
  /**
   * `client_photo_uuid` — the IndexedDB key AND the `X-Idempotency-Key`
   * reused for every upload attempt of this photo, so a retry can never
   * attach the photo twice.
   */
  photoId: string;
  /** Server record id this photo is appended to (a real, synced record). */
  recordId: string;
  projectId: string;
  /** Client-rendered annotated bytes — the multipart `image` field. */
  annotatedBlob: Blob;
  /** Original raw photo — the multipart `original` field. */
  originalBlob: Blob;
  /** Annotations payload — serialized into the `annotations` field. */
  annotations: PendingAppendAnnotations;
  /** Hint for the `taken_at_hint` field; `taken_at_source_hint` is derived. */
  takenAtHint: string | null;
  status: PendingAppendStatus;
  createdAt: number;
  /** Set to the failure message when a permanent (4xx) rejection lands. */
  error?: string | null;
}

/**
 * The on-disk `pendingAppends` row. Image bytes are stored as inline
 * `ArrayBuffer`s with their MIME type as a sibling string — exactly like
 * {@link StoredPendingRecord} (see {@link DB_VERSION}). This store is brand
 * new in v4, so there are no legacy `Blob` rows: the blob fields are always
 * `ArrayBuffer`, but typed `ArrayBuffer | Blob` to share the
 * {@link fromStoredBytes} read path with `pendingRecords`.
 */
interface StoredPendingAppend
  extends Omit<PendingAppend, "annotatedBlob" | "originalBlob"> {
  annotatedBlob: ArrayBuffer | Blob;
  annotatedBlobType?: string;
  originalBlob: ArrayBuffer | Blob;
  originalBlobType?: string;
}

export type PendingDeleteStatus = "pending" | "failed";

/**
 * A locally-queued per-photo DELETE of a photo on an EXISTING, synced
 * multi-photo record. The delete outbox is a durable sibling of
 * `pendingAppends` (see [[delete-photo-outbox]]): same retry-forever /
 * pause-offline discipline, but the opposite optimistic model — it REMOVES a
 * tile rather than augmenting the record. The drain replays `pending` entries
 * until each photo is gone from the server's v2.0 record.
 *
 * Carries no blobs — a delete needs only the ids. `photoId` is the
 * ProjectImage id (NOT the `client_photo_uuid`); it is the IndexedDB key, so a
 * second queue of the same photo simply overwrites the entry (idempotent at the
 * outbox level too). The server DELETE is HTTP-idempotent and a 404 is treated
 * as success (already deleted).
 */
export interface PendingDelete {
  /** The ProjectImage id being removed — the IndexedDB key. */
  photoId: string;
  /** Server record id the photo belongs to (a real, synced record). */
  recordId: string;
  projectId: string;
  status: PendingDeleteStatus;
  createdAt: number;
  /** Set to the failure message when a permanent (4xx) rejection lands. */
  error?: string | null;
}

interface OtusOfflineSchema extends DBSchema {
  identity: {
    key: "current";
    value: CachedIdentity;
  };
  pinnedProjects: {
    key: string;
    value: PinnedProject;
  };
  assets: {
    key: string;
    value: StoredAssetRow;
    indexes: { "by-project": string };
  };
  queryClientCache: {
    key: "cache";
    value: unknown;
  };
  pendingRecords: {
    key: string;
    value: StoredPendingRecord;
    indexes: { "by-project": string };
  };
  pendingAppends: {
    key: string;
    value: StoredPendingAppend;
    indexes: { "by-record": string; "by-project": string };
  };
  pendingDeletes: {
    key: string;
    value: PendingDelete;
    indexes: { "by-record": string; "by-project": string };
  };
}

let dbPromise: Promise<IDBPDatabase<OtusOfflineSchema>> | null = null;

function getDB(): Promise<IDBPDatabase<OtusOfflineSchema>> {
  if (!dbPromise) {
    const opening = openDB<OtusOfflineSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains("identity")) {
          db.createObjectStore("identity");
        }
        if (!db.objectStoreNames.contains("pinnedProjects")) {
          db.createObjectStore("pinnedProjects", { keyPath: "projectId" });
        }
        // pre-v3 → v3: the `assets` rows changed from raw `Blob` to inline
        // `ArrayBuffer`. Drop the store so no pre-v3 `Blob` rows survive — the
        // asset cache is disposable and re-downloads on the next pin/resync.
        // `pendingRecords` is deliberately NOT dropped (un-uploaded captures);
        // its read path tolerates legacy `Blob` rows instead.
        if (db.objectStoreNames.contains("assets") && oldVersion < 3) {
          db.deleteObjectStore("assets");
        }
        if (!db.objectStoreNames.contains("assets")) {
          const store = db.createObjectStore("assets", { keyPath: "key" });
          store.createIndex("by-project", "projectId");
        }
        if (!db.objectStoreNames.contains("queryClientCache")) {
          db.createObjectStore("queryClientCache");
        }
        if (!db.objectStoreNames.contains("pendingRecords")) {
          const store = db.createObjectStore("pendingRecords", {
            keyPath: "clientUuid",
          });
          store.createIndex("by-project", "projectId");
        }
        // v4: the append outbox. Guarded by `contains` (not an `oldVersion`
        // check) so it is created on a fresh DB AND on an upgrade from any
        // prior version, while every existing store is left untouched.
        if (!db.objectStoreNames.contains("pendingAppends")) {
          const store = db.createObjectStore("pendingAppends", {
            keyPath: "photoId",
          });
          store.createIndex("by-record", "recordId");
          store.createIndex("by-project", "projectId");
        }
        // v5: the per-photo delete outbox. Guarded by `contains` (not an
        // `oldVersion` check) so it is created on a fresh DB AND on an upgrade
        // from any prior version, while every existing store is left untouched.
        if (!db.objectStoreNames.contains("pendingDeletes")) {
          const store = db.createObjectStore("pendingDeletes", {
            keyPath: "photoId",
          });
          store.createIndex("by-record", "recordId");
          store.createIndex("by-project", "projectId");
        }
      },
    });
    // A failed `openDB` means the WHOLE offline layer is dead for this user —
    // no cache, no outbox, nothing persists. Capture it ONCE (this `.catch`
    // is attached to the shared `opening` promise, so it fires a single
    // event no matter how many callers await `getDB()`), then drop the
    // memoized rejected promise so a later call can retry a transient open
    // failure. The rejection still propagates to every caller — this
    // side-effect `.catch` does not swallow it (see [[error-reporting]]).
    opening.catch((err) => {
      Sentry.captureException(err, {
        level: "error",
        tags: { area: "offline-cache", phase: "idb-open" },
      });
      dbPromise = null;
    });
    dbPromise = opening;
  }
  return dbPromise;
}

/**
 * Read a Blob's bytes into an `ArrayBuffer` for inline IndexedDB storage,
 * capturing its MIME type alongside. See {@link DB_VERSION} for why raw Blobs
 * are never persisted.
 */
async function toStoredBytes(
  blob: Blob,
): Promise<{ data: ArrayBuffer; type: string }> {
  return {
    data: await blob.arrayBuffer(),
    type: blob.type || DEFAULT_BLOB_TYPE,
  };
}

/**
 * Rebuild a Blob from a stored field. New rows hold an `ArrayBuffer` + type; a
 * legacy v2 `pendingRecords` row still holds a raw `Blob`, returned as-is.
 */
function fromStoredBytes(
  value: ArrayBuffer | Blob,
  type: string | undefined,
): Blob {
  if (value instanceof Blob) return value;
  return new Blob([value], { type: type ?? DEFAULT_BLOB_TYPE });
}

/** Serialize one pending photo to its on-disk shape (image bytes → ArrayBuffer). */
async function toStoredPendingPhoto(
  photo: PendingPhoto,
): Promise<StoredPendingPhoto> {
  const annotated = await toStoredBytes(photo.annotatedBlob);
  const original = await toStoredBytes(photo.originalBlob);
  return {
    ...photo,
    annotatedBlob: annotated.data,
    annotatedBlobType: annotated.type,
    originalBlob: original.data,
    originalBlobType: original.type,
  };
}

/** Rehydrate one stored pending photo to the `PendingPhoto` shape. */
function fromStoredPendingPhoto(stored: StoredPendingPhoto): PendingPhoto {
  const { annotatedBlobType, originalBlobType, ...rest } = stored;
  return {
    ...rest,
    annotatedBlob: fromStoredBytes(stored.annotatedBlob, annotatedBlobType),
    originalBlob: fromStoredBytes(stored.originalBlob, originalBlobType),
  };
}

/** Serialize a pending record to its on-disk shape (image bytes → ArrayBuffer). */
async function toStoredPendingRecord(
  record: PendingRecord,
): Promise<StoredPendingRecord> {
  const { photos, ...rest } = record;
  const annotated = await toStoredBytes(record.annotatedBlob);
  const original = await toStoredBytes(record.originalBlob);
  return {
    ...rest,
    annotatedBlob: annotated.data,
    annotatedBlobType: annotated.type,
    originalBlob: original.data,
    originalBlobType: original.type,
    ...(photos
      ? { photos: await Promise.all(photos.map(toStoredPendingPhoto)) }
      : {}),
  };
}

/** Rehydrate a stored pending record to the `PendingRecord` shape callers expect. */
function fromStoredPendingRecord(stored: StoredPendingRecord): PendingRecord {
  const { annotatedBlobType, originalBlobType, photos, ...rest } = stored;
  const entry: PendingRecord = {
    ...rest,
    annotatedBlob: fromStoredBytes(stored.annotatedBlob, annotatedBlobType),
    originalBlob: fromStoredBytes(stored.originalBlob, originalBlobType),
    ...(photos ? { photos: photos.map(fromStoredPendingPhoto) } : {}),
  };
  // Legacy normalization: a stored row written by the pre-multi-photo build has
  // no `photos` field. Synthesize a 1-photo array from the top-level cover
  // bytes so every downstream consumer (drain, viewer, append) can assume
  // `photos` is always present and non-empty. `photoId` = `clientUuid` matches
  // the `X-Idempotency-Key` the old v1.2 single-photo upload used, so a retry
  // cannot double-attach. The stored row is NOT mutated — this is read-time only.
  if (!entry.photos || entry.photos.length === 0) {
    // Legacy cover seed (IN-MEMORY shape — rehydrated `Blob`s, no *BlobType
    // siblings). Keep field-for-field in sync with the seed in
    // `appendPhotosToPendingRecord`; that one emits the STORED variant
    // (`ArrayBuffer`s + annotatedBlobType / originalBlobType).
    entry.photos = [
      {
        photoId: entry.clientUuid,
        annotatedBlob: entry.annotatedBlob,
        originalBlob: entry.originalBlob,
        originalFileName: entry.originalFileName,
        annotations: entry.annotations,
        takenAtHint: entry.takenAtHint,
        uploadStatus: "pending",
      },
    ];
  }
  return entry;
}

/** Serialize a pending append to its on-disk shape (image bytes → ArrayBuffer). */
async function toStoredPendingAppend(
  append: PendingAppend,
): Promise<StoredPendingAppend> {
  const annotated = await toStoredBytes(append.annotatedBlob);
  const original = await toStoredBytes(append.originalBlob);
  return {
    ...append,
    annotatedBlob: annotated.data,
    annotatedBlobType: annotated.type,
    originalBlob: original.data,
    originalBlobType: original.type,
  };
}

/** Rehydrate a stored pending append to the `PendingAppend` shape callers expect. */
function fromStoredPendingAppend(stored: StoredPendingAppend): PendingAppend {
  const { annotatedBlobType, originalBlobType, ...rest } = stored;
  return {
    ...rest,
    annotatedBlob: fromStoredBytes(stored.annotatedBlob, annotatedBlobType),
    originalBlob: fromStoredBytes(stored.originalBlob, originalBlobType),
  };
}

export async function getIdentity(): Promise<CachedIdentity | undefined> {
  const db = await getDB();
  return db.get("identity", "current");
}

export async function putIdentity(identity: CachedIdentity): Promise<void> {
  const db = await getDB();
  await db.put("identity", identity, "current");
}

export async function deleteIdentity(): Promise<void> {
  const db = await getDB();
  await db.delete("identity", "current");
}

export async function getPinnedProject(
  projectId: string,
): Promise<PinnedProject | undefined> {
  const db = await getDB();
  return db.get("pinnedProjects", projectId);
}

export async function getAllPinnedProjects(): Promise<PinnedProject[]> {
  const db = await getDB();
  return db.getAll("pinnedProjects");
}

export async function putPinnedProject(project: PinnedProject): Promise<void> {
  const db = await getDB();
  await db.put("pinnedProjects", project);
}

export async function deletePinnedProject(projectId: string): Promise<void> {
  const db = await getDB();
  await db.delete("pinnedProjects", projectId);
}

export async function getAsset(key: string): Promise<StoredAsset | undefined> {
  const db = await getDB();
  const row = await db.get("assets", key);
  if (!row) return undefined;
  return {
    key: row.key,
    blob: new Blob([row.data], { type: row.contentType }),
    contentType: row.contentType,
    fetchedAt: row.fetchedAt,
    projectId: row.projectId,
    provisional: row.provisional,
  };
}

export async function putAsset(asset: StoredAsset): Promise<void> {
  const db = await getDB();
  await db.put("assets", {
    key: asset.key,
    data: await asset.blob.arrayBuffer(),
    contentType: asset.contentType,
    fetchedAt: asset.fetchedAt,
    projectId: asset.projectId,
    provisional: asset.provisional,
  });
}

export async function deleteAsset(key: string): Promise<void> {
  const db = await getDB();
  await db.delete("assets", key);
}

export async function getAssetKeysByProject(
  projectId: string,
): Promise<string[]> {
  const db = await getDB();
  return db.getAllKeysFromIndex("assets", "by-project", projectId);
}

/**
 * Lightweight metadata scan over a primary-key prefix range — used by the
 * tile cache's trim-on-write to size a revision's tile footprint without
 * materializing blobs. `￿` (U+FFFF) upper-bounds the range over all suffixes.
 */
export async function getAssetMetasByPrefix(
  prefix: string,
): Promise<Array<{ key: string; fetchedAt: number; sizeBytes: number }>> {
  const db = await getDB();
  const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
  const metas: Array<{ key: string; fetchedAt: number; sizeBytes: number }> = [];
  let cursor = await db.transaction("assets").store.openCursor(range);
  while (cursor) {
    metas.push({
      key: cursor.value.key,
      fetchedAt: cursor.value.fetchedAt,
      sizeBytes: cursor.value.data.byteLength,
    });
    cursor = await cursor.continue();
  }
  return metas;
}

export async function deleteAssetsByProject(projectId: string): Promise<number> {
  const db = await getDB();
  const tx = db.transaction("assets", "readwrite");
  const index = tx.store.index("by-project");
  let deleted = 0;
  for await (const cursor of index.iterate(projectId)) {
    await cursor.delete();
    deleted++;
  }
  await tx.done;
  return deleted;
}

export async function getQueryCache(): Promise<unknown> {
  const db = await getDB();
  return db.get("queryClientCache", "cache");
}

export async function putQueryCache(value: unknown): Promise<void> {
  const db = await getDB();
  await db.put("queryClientCache", value, "cache");
}

export async function deleteQueryCache(): Promise<void> {
  const db = await getDB();
  await db.delete("queryClientCache", "cache");
}

export async function getPendingRecord(
  clientUuid: string,
): Promise<PendingRecord | undefined> {
  const db = await getDB();
  const stored = await db.get("pendingRecords", clientUuid);
  return stored ? fromStoredPendingRecord(stored) : undefined;
}

/**
 * {@link getPendingRecord} for a caller choosing between the local outbox and
 * the server — "is this record a local draft?" — that must not be stopped by an
 * outbox it cannot read.
 *
 * When the offline database will not open at all (a `VersionError` because a
 * newer build already upgraded it, storage disabled, a corrupt profile), the
 * answer is "not a draft" rather than a throw. That is safe: with the outbox
 * unreadable, no draft can be listed or opened either, so the record in hand
 * came from the server. Without this, saving an edit or deleting a synced
 * record died silently on such a browser. See [[offline-record-outbox]]
 * § Rules.
 *
 * The open failure itself is already reported by `getDB`; this adds a warning
 * that an action fell back, so the two together say how often it matters.
 */
export async function findPendingRecord(
  clientUuid: string,
): Promise<PendingRecord | undefined> {
  try {
    return await getPendingRecord(clientUuid);
  } catch (err) {
    console.warn("Outbox unreadable; treating record as synced:", err);
    Sentry.captureException(err, {
      level: "warning",
      tags: { area: "outbox", phase: "lookup-fallback" },
    });
    return undefined;
  }
}

export async function getAllPendingRecords(): Promise<PendingRecord[]> {
  const db = await getDB();
  const stored = await db.getAll("pendingRecords");
  return stored.map(fromStoredPendingRecord);
}

export async function getPendingRecordsByProject(
  projectId: string,
): Promise<PendingRecord[]> {
  const db = await getDB();
  const stored = await db.getAllFromIndex(
    "pendingRecords",
    "by-project",
    projectId,
  );
  return stored.map(fromStoredPendingRecord);
}

export async function putPendingRecord(record: PendingRecord): Promise<void> {
  const db = await getDB();
  await db.put("pendingRecords", await toStoredPendingRecord(record));
}

export async function updatePendingRecordStatus(
  clientUuid: string,
  status: PendingRecordStatus,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing) {
    await tx.store.put({ ...existing, status });
  }
  await tx.done;
}

/**
 * Set one photo's `uploadStatus` (and optional error) within a multi-photo
 * record entry. Read-modify-write inside one transaction; only the status /
 * error change — the stored blob bytes are left untouched, so no
 * re-serialization is needed. A no-op if the entry or photo is gone.
 *
 * Concurrent callers (the drain marks several photos `synced` in parallel) are
 * safe: IndexedDB serializes overlapping-scope `readwrite` transactions, so the
 * per-photo read-modify-writes commit in creation order and never clobber each
 * other — the same property `updatePendingRecordStatus` relies on.
 */
export async function updatePendingPhotoStatus(
  clientUuid: string,
  photoId: string,
  status: PendingPhotoStatus,
  error?: string | null,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing?.photos) {
    const photos = existing.photos.map((photo) =>
      photo.photoId === photoId
        ? { ...photo, uploadStatus: status, error: error ?? null }
        : photo,
    );
    await tx.store.put({ ...existing, photos });
  }
  await tx.done;
}

/**
 * Record the server record id of a multi-photo entry the first time one of its
 * photos syncs. Writes once: a no-op if the entry is gone or the id is already
 * set (the backend returns the same id for every photo of one
 * `client_record_uuid`), so the drain's concurrent per-photo callers don't
 * thrash it. Used so a later delete can compensate by id without a re-POST.
 */
export async function setPendingRecordServerId(
  clientUuid: string,
  serverRecordId: string,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing && !existing.serverRecordId) {
    await tx.store.put({ ...existing, serverRecordId });
  }
  await tx.done;
}

/**
 * Reset an entry for tap-to-retry: entry status → `pending`, and for a
 * multi-photo record every `failed` photo → `pending` (clearing its error),
 * while `synced` photos are left as-is so the retry re-uploads only what
 * failed. For a single-photo entry this is exactly
 * `updatePendingRecordStatus(_, "pending")`.
 */
export async function resetPendingRecordForRetry(
  clientUuid: string,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing) {
    const photos = existing.photos?.map((photo) =>
      photo.uploadStatus === "failed"
        ? { ...photo, uploadStatus: "pending" as const, error: null }
        : photo,
    );
    await tx.store.put({
      ...existing,
      status: "pending",
      ...(photos ? { photos } : {}),
    });
  }
  await tx.done;
}

/**
 * Merges a partial of the editable fields into a queued record and
 * increments its `revision`. Used when a pending record is edited as a
 * local draft before it syncs.
 */
export async function updatePendingRecordFields(
  clientUuid: string,
  partial: Partial<PendingRecordEditableFields>,
): Promise<void> {
  const db = await getDB();
  // Serialize any Blob fields in the patch BEFORE opening the transaction:
  // `Blob.arrayBuffer()` is async, and awaiting a non-IDB promise inside an
  // IDB transaction lets it auto-commit, which makes the later `put` throw.
  const { annotatedBlob, originalBlob, ...rest } = partial;
  const storedPatch: Partial<StoredPendingRecord> = { ...rest };
  if (annotatedBlob) {
    const annotated = await toStoredBytes(annotatedBlob);
    storedPatch.annotatedBlob = annotated.data;
    storedPatch.annotatedBlobType = annotated.type;
  }
  if (originalBlob) {
    const original = await toStoredBytes(originalBlob);
    storedPatch.originalBlob = original.data;
    storedPatch.originalBlobType = original.type;
  }
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing) {
    const next: StoredPendingRecord = {
      ...existing,
      ...storedPatch,
      revision: existing.revision + 1,
    };
    // Multi-photo invariant: the top-level cover bytes MIRROR photos[0]. A
    // markup re-edit of a queued multi draft patches the top-level cover
    // (annotated / original / annotations) through this fn; mirror those
    // cover-byte fields into photos[0] too, or the per-photo drain (which
    // uploads from photos[]) would ship the STALE cover and silently drop the
    // edit (Codex P2). Metadata edits are NOT mirrored — they don't live in
    // photos[]. Only the cover (index 0) is mirrored; non-cover per-photo
    // re-edit is out of scope (FE-DEBT-009).
    if (existing.photos && existing.photos.length > 0) {
      const coverPatch: Record<string, unknown> = {};
      for (const key of [
        "annotatedBlob",
        "annotatedBlobType",
        "originalBlob",
        "originalBlobType",
        "originalFileName",
        "annotations",
        "takenAtHint",
      ] as const) {
        if (key in storedPatch) {
          coverPatch[key] = (storedPatch as Record<string, unknown>)[key];
        }
      }
      if (Object.keys(coverPatch).length > 0) {
        next.photos = existing.photos.map((photo, i) =>
          i === 0 ? { ...photo, ...coverPatch } : photo,
        );
      }
    }
    await tx.store.put(next);
  }
  await tx.done;
}

/**
 * Appends one or more photos to a queued (pending+offline) record's `photos[]`
 * and bumps its `revision`, so the outbox drain picks them up alongside the
 * record's existing photos in the shared v2.0 multi-photo upload.
 *
 * Handles both row shapes:
 * - **New-build rows** (written post-PR1): `existing.photos` is present; the
 *   incoming photos are appended after the existing ones.
 * - **Legacy rows** (written before multi-photo): no `photos` in storage; the
 *   original photo is synthesized from the top-level cover fields exactly as
 *   {@link fromStoredPendingRecord} does at read-time (so the original is never
 *   lost), then the incoming photos follow.
 *
 * Every incoming photo is stored `pending` (a freshly appended photo is always
 * un-uploaded). The top-level cover fields are NOT changed — they always mirror
 * `photos[0]`, which is untouched by this operation. A no-op if the entry is
 * gone. Blob serialization is done BEFORE opening the transaction (awaiting a
 * non-IDB promise inside a transaction auto-commits it).
 */
export async function appendPhotosToPendingRecord(
  clientUuid: string,
  photos: PendingPhoto[],
): Promise<void> {
  // Force every incoming photo to `pending` — a freshly appended photo is
  // always un-uploaded, regardless of the uploadStatus the caller supplies.
  // Serialize blobs BEFORE the transaction — async IDB operations inside a tx
  // auto-commit it, making the later put() throw.
  const serializedNew = await Promise.all(
    photos
      .map((p) => ({ ...p, uploadStatus: "pending" as const }))
      .map(toStoredPendingPhoto),
  );

  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (!existing) {
    await tx.done;
    return;
  }

  // Determine the base photos array: use the stored photos[] if present,
  // otherwise synthesize the legacy cover photo from the top-level fields —
  // mirrors the normalization in fromStoredPendingRecord exactly so the
  // original capture is never dropped.
  const basePhotos: StoredPendingPhoto[] =
    existing.photos && existing.photos.length > 0
      ? existing.photos
      : // Legacy cover seed (STORED shape — carries annotatedBlobType /
        // originalBlobType siblings). Keep field-for-field in sync with the
        // seed in `fromStoredPendingRecord`; that one emits the IN-MEMORY
        // variant (rehydrated `Blob`s, no *BlobType siblings).
        [
          {
            photoId: existing.clientUuid,
            annotatedBlob: existing.annotatedBlob,
            annotatedBlobType: existing.annotatedBlobType,
            originalBlob: existing.originalBlob,
            originalBlobType: existing.originalBlobType,
            originalFileName: existing.originalFileName,
            annotations: existing.annotations,
            takenAtHint: existing.takenAtHint,
            uploadStatus: "pending",
          },
        ];

  await tx.store.put({
    ...existing,
    photos: [...basePhotos, ...serializedNew],
    revision: existing.revision + 1,
  });
  await tx.done;
}

/**
 * Removes ONE photo from a queued MULTI-photo draft by its `photoId`
 * (= `client_photo_uuid`). Used by the image viewer's per-photo delete on a
 * pending offline draft: the photo lives only inside this outbox entry's
 * `photos[]`, so removing it is a local splice — no server call (the draft was
 * never uploaded). Mirrors `appendPhotosToPendingRecord`'s stored-level
 * transaction.
 *
 * If the removed photo is the COVER (`photos[0]`), the entry's top-level
 * `annotatedBlob` / `originalBlob` / `originalFileName` / `annotations` /
 * `takenAtHint` are re-mirrored onto the NEW `photos[0]` so the invariant
 * "top-level fields mirror the cover" holds — otherwise the grid tile and the
 * modal cover would keep rendering the deleted photo's bytes. Bumps `revision`
 * so `usePendingRecords` re-mints the cover object URL.
 *
 * Guards: a no-op if the entry is gone, if `photoId` is not found, or if the
 * removal would empty `photos[]` (the lone photo of a draft is a record delete,
 * routed separately — this never silently destroys the last photo). Blob bytes
 * are moved between stored fields as-is; nothing is re-serialized.
 *
 * REFUSES a `synced` photo. A multi-photo upload that partially succeeds and
 * then goes offline leaves some photos `synced` (already POSTed under this
 * entry's `serverRecordId`) while the draft stays `pending`. Splicing such a
 * photo out of `photos[]` would drop it locally with NO compensating server
 * DELETE, so once the remaining photos finish uploading the converged server
 * record still carries the image and the "deleted" photo reappears (Codex P1).
 * A synced photo can only be removed online, by server image id, once the
 * record finishes syncing and becomes an ordinary synced record — so we leave
 * the entry untouched and tell the caller (`"synced"`) to surface that.
 *
 * Returns what happened so the caller can refresh the queue on a real removal
 * or message the user when the removal was refused.
 */
export async function removePhotoFromPendingRecord(
  clientUuid: string,
  photoId: string,
): Promise<RemovePendingPhotoResult> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (!existing || !existing.photos || existing.photos.length === 0) {
    await tx.done;
    return "not-found";
  }
  const basePhotos = existing.photos;
  const target = basePhotos.find((p) => p.photoId === photoId);
  if (!target) {
    await tx.done;
    return "not-found";
  }
  // Already on the server (partial-sync window) — refuse the local splice so a
  // deleted photo can't silently reappear when the rest of the batch lands.
  if (target.uploadStatus === "synced") {
    await tx.done;
    return "synced";
  }
  const filtered = basePhotos.filter((p) => p.photoId !== photoId);
  // Would empty the entry (the last photo is a record delete, handled by the
  // caller) — leave the entry untouched.
  if (filtered.length === 0) {
    await tx.done;
    return "last-photo";
  }
  // If the cover was removed, re-mirror the top-level fields onto the new cover
  // (filtered[0]) so the entry's "top-level mirrors photos[0]" invariant holds.
  const coverRemoved = basePhotos[0].photoId === photoId;
  const newCover = filtered[0];
  const coverMirror: Partial<StoredPendingRecord> = coverRemoved
    ? {
        annotatedBlob: newCover.annotatedBlob,
        annotatedBlobType: newCover.annotatedBlobType,
        originalBlob: newCover.originalBlob,
        originalBlobType: newCover.originalBlobType,
        originalFileName: newCover.originalFileName,
        annotations: newCover.annotations,
        takenAtHint: newCover.takenAtHint,
      }
    : {};
  await tx.store.put({
    ...existing,
    ...coverMirror,
    photos: filtered,
    revision: existing.revision + 1,
  });
  await tx.done;
  return "removed";
}

/**
 * Re-writes ONE photo's markup fields (annotated bytes, original bytes,
 * annotations) inside a queued draft — the per-photo markup re-edit for
 * not-yet-uploaded records (see [[image-viewer-modal]] US-003c).
 *
 * `photoId` is the photo's `client_photo_uuid`; pass `null` to target the
 * cover (`photos[0]`, or the top-level fields of a legacy entry that predates
 * `photos[]`). Editing the cover also re-writes the top-level mirror fields —
 * the "top-level mirrors photos[0]" invariant `updatePendingRecordFields`
 * maintains. `takenAtHint` / `uploadStatus` are untouched (a re-edit never
 * alters capture time or upload progress), and `revision` bumps so the drain's
 * mid-upload reconcile catches a cover edit that landed after its snapshot.
 *
 * A photo that already uploaded during a partial sync is refused with
 * `"synced"` (mirrors {@link removePhotoFromPendingRecord}): the drain cannot
 * replay a non-cover markup edit, so accepting the write would silently lose
 * it once the rest of the batch lands.
 */
export async function updatePendingRecordPhoto(
  clientUuid: string,
  photoId: string | null,
  patch: PendingPhotoMarkupPatch,
): Promise<UpdatePendingPhotoResult> {
  const db = await getDB();
  // Serialize the Blob fields BEFORE opening the transaction:
  // `Blob.arrayBuffer()` is async, and awaiting a non-IDB promise inside an
  // IDB transaction lets it auto-commit, which makes the later `put` throw
  // (same rule as `updatePendingRecordFields`).
  const annotated = await toStoredBytes(patch.annotatedBlob);
  const original = await toStoredBytes(patch.originalBlob);
  const storedPatch = {
    annotatedBlob: annotated.data,
    annotatedBlobType: annotated.type,
    originalBlob: original.data,
    originalBlobType: original.type,
    originalFileName: patch.originalFileName,
    annotations: patch.annotations,
  };
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (!existing) {
    await tx.done;
    return "not-found";
  }
  const photos = existing.photos ?? [];
  // Resolve the target: an explicit per-photo id, or the cover when null. A
  // legacy entry with no photos[] only ever has its cover to edit — any
  // photoId resolves to the top-level fields there.
  const index =
    photoId == null ? 0 : photos.findIndex((p) => p.photoId === photoId);
  if (photos.length > 0 && index === -1) {
    await tx.done;
    return "not-found";
  }
  const target = photos[index] as StoredPendingPhoto | undefined;
  if (target?.uploadStatus === "synced") {
    await tx.done;
    return "synced";
  }
  const nextPhotos = target
    ? photos.map((p, i) => (i === index ? { ...p, ...storedPatch } : p))
    : photos;
  const isCover = target == null || index === 0;
  await tx.store.put({
    ...existing,
    ...(isCover ? storedPatch : {}),
    ...(existing.photos ? { photos: nextPhotos } : {}),
    revision: existing.revision + 1,
  });
  await tx.done;
  return "updated";
}

/**
 * Marks a queued record `deleted` without removing it — used for the
 * in-flight-upload reconcile so a delete that lands mid-upload can be
 * turned into a compensating server `DELETE` after the create settles.
 */
export async function markPendingRecordDeleted(
  clientUuid: string,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingRecords", "readwrite");
  const existing = await tx.store.get(clientUuid);
  if (existing) {
    await tx.store.put({ ...existing, deleted: true });
  }
  await tx.done;
}

export async function deletePendingRecord(clientUuid: string): Promise<void> {
  const db = await getDB();
  await db.delete("pendingRecords", clientUuid);
}

export async function putPendingAppend(append: PendingAppend): Promise<void> {
  const db = await getDB();
  await db.put("pendingAppends", await toStoredPendingAppend(append));
}

export async function getPendingAppendsByRecord(
  recordId: string,
): Promise<PendingAppend[]> {
  const db = await getDB();
  const stored = await db.getAllFromIndex(
    "pendingAppends",
    "by-record",
    recordId,
  );
  return stored.map(fromStoredPendingAppend);
}

export async function getPendingAppendsByProject(
  projectId: string,
): Promise<PendingAppend[]> {
  const db = await getDB();
  const stored = await db.getAllFromIndex(
    "pendingAppends",
    "by-project",
    projectId,
  );
  return stored.map(fromStoredPendingAppend);
}

export async function getAllPendingAppends(): Promise<PendingAppend[]> {
  const db = await getDB();
  const stored = await db.getAll("pendingAppends");
  return stored.map(fromStoredPendingAppend);
}

export async function updatePendingAppendStatus(
  photoId: string,
  status: PendingAppendStatus,
  error?: string | null,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingAppends", "readwrite");
  const existing = await tx.store.get(photoId);
  if (existing) {
    // `error` is set only on a failure and cleared on a return-to-pending
    // (tap-to-retry), so a stale message never lingers on a re-queued photo.
    await tx.store.put({ ...existing, status, error: error ?? null });
  }
  await tx.done;
}

export async function deletePendingAppend(photoId: string): Promise<void> {
  const db = await getDB();
  await db.delete("pendingAppends", photoId);
}

/**
 * Purge every queued append for a record — used when a synced record is
 * deleted while it still has photos in the append outbox, so no append is
 * ever fired against a record the server no longer has. See
 * [[append-photo-outbox]].
 */
export async function deletePendingAppendsByRecord(
  recordId: string,
): Promise<number> {
  const db = await getDB();
  const tx = db.transaction("pendingAppends", "readwrite");
  const index = tx.store.index("by-record");
  let deleted = 0;
  for await (const cursor of index.iterate(recordId)) {
    await cursor.delete();
    deleted++;
  }
  await tx.done;
  return deleted;
}

export async function putPendingDelete(del: PendingDelete): Promise<void> {
  const db = await getDB();
  await db.put("pendingDeletes", del);
}

export async function getPendingDeletesByRecord(
  recordId: string,
): Promise<PendingDelete[]> {
  const db = await getDB();
  return db.getAllFromIndex("pendingDeletes", "by-record", recordId);
}

export async function getPendingDeletesByProject(
  projectId: string,
): Promise<PendingDelete[]> {
  const db = await getDB();
  return db.getAllFromIndex("pendingDeletes", "by-project", projectId);
}

export async function getAllPendingDeletes(): Promise<PendingDelete[]> {
  const db = await getDB();
  return db.getAll("pendingDeletes");
}

export async function updatePendingDeleteStatus(
  photoId: string,
  status: PendingDeleteStatus,
  error?: string | null,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("pendingDeletes", "readwrite");
  const existing = await tx.store.get(photoId);
  if (existing) {
    // `error` is set only on a failure and cleared on a return-to-pending
    // (tap-to-retry), so a stale message never lingers on a re-queued delete.
    await tx.store.put({ ...existing, status, error: error ?? null });
  }
  await tx.done;
}

export async function deletePendingDelete(photoId: string): Promise<void> {
  const db = await getDB();
  await db.delete("pendingDeletes", photoId);
}

/**
 * Purge every queued per-photo delete for a record — used when a synced record
 * is itself deleted while it still has photos in the delete outbox, so no
 * per-photo delete is ever fired against a record the server no longer has.
 * Mirrors {@link deletePendingAppendsByRecord}. See [[delete-photo-outbox]].
 */
export async function deletePendingDeletesByRecord(
  recordId: string,
): Promise<number> {
  const db = await getDB();
  const tx = db.transaction("pendingDeletes", "readwrite");
  const index = tx.store.index("by-record");
  let deleted = 0;
  for await (const cursor of index.iterate(recordId)) {
    await cursor.delete();
    deleted++;
  }
  await tx.done;
  return deleted;
}

/**
 * Wipes everything in the offline DB. Called on sign-out and when a fresh
 * WorkOS validation returns a user whose id differs from the cached identity.
 */
export async function clearAllOfflineStores(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(
    [
      "identity",
      "pinnedProjects",
      "assets",
      "queryClientCache",
      "pendingRecords",
      "pendingAppends",
      "pendingDeletes",
    ],
    "readwrite",
  );
  await Promise.all([
    tx.objectStore("identity").clear(),
    tx.objectStore("pinnedProjects").clear(),
    tx.objectStore("assets").clear(),
    tx.objectStore("queryClientCache").clear(),
    tx.objectStore("pendingRecords").clear(),
    tx.objectStore("pendingAppends").clear(),
    tx.objectStore("pendingDeletes").clear(),
  ]);
  await tx.done;
}
